<!-- The preview on its way, in the preview step's own layout (Figma 28516:21496): the same devices
     with empty screens, and the card listing what is happening. No frame before the render ends. -->
<div class="order-layout" *ngIf="job && !isRendering">
  <section class="order-main">
    <h1 class="flow-title">Finding the best light.</h1>
    <p class="flow-subtitle">Setting up the scene for <span class="rf-name-with"><strong class="rf-name" [title]="job.name">{{ job.name }}</strong>.</span><br>Both frames appear right here once they are rendered.</p>

    <div class="order-devices" [class.has-note]="!!uploadNote">
      <app-render-still-devices [pending]="screenState" [sides]="job.sides" [name]="job.name"></app-render-still-devices>
      <p class="rf-fine-print stand-in-note" *ngIf="uploadNote">{{ uploadNote }}</p>
    </div>
  </section>

  <aside class="order-card order-card--progress">
    <h2>Your preview is on its way.</h2>
    <!-- From the upload's first byte (owner, 2026-10-20): uploading, the studio, the model checked by
         the renderer, the frames (decision 40) — the same words as the screens, the library, the
         header and the tab. -->
    <ol class="preview-stages preview-stages--live">
      <!-- Each line closes as the bar reaches its end: the spinning arc closes into a ring, the
           check lands, then the next line starts (owner, 2026-10-20: no jumps). The words fade in
           as they change. -->
      <li *ngFor="let row of rows; let i = index" class="preview-stages__item" [attr.data-state]="rowState(i)">
        <svg class="stage-mark" width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
          <circle class="stage-mark__ring" cx="11" cy="11" r="10"/>
          <circle class="stage-mark__arc" cx="11" cy="11" r="10" pathLength="100"/>
          <circle class="stage-mark__disc" cx="11" cy="11" r="11"/>
          <path class="stage-mark__check" d="M5.75 11.7 8.9 14.86 16.25 7.5" pathLength="1"/>
        </svg>
        <span class="preview-stages__label"><span *ngFor="let label of [rowLabel(i)]" class="preview-stages__words">{{ label }}</span></span>
        <ng-container *ngFor="let state of [rowState(i)]"><small *ngIf="rowDetail(i) as detail" class="preview-stages__detail">{{ detail }}</small></ng-container>
      </li>
    </ol>

    <div class="order-card__progress">
      <div class="progress-track" role="progressbar" aria-label="Preview preparation" aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="barProgress">
        <div #barFill class="progress-fill progress-fill--glide"></div>
      </div>
      <p class="order-card__fine">{{ eta }}</p>
      <span class="sr-only" role="status">{{ job.statusText }}</span>
      <!-- The upload needs this tab; the render goes on without the page: the header's ring shows
           it, its dot calls back. -->
      <p *ngIf="isUploading; else leaveFree" class="order-card__leave"><span class="order-card__leave-ring" aria-hidden="true"></span><span>Keep this tab open until the upload is complete. Closing it stops the upload. You can look around the site meanwhile.</span></p>
      <ng-template #leaveFree><p class="order-card__leave"><span class="order-card__leave-ring" aria-hidden="true"></span><span>You can leave this page{{ runsWithoutPage ? ' or close the tab' : '' }}. The ring on your profile shows the progress, and the preview waits in <a routerLink="/render/library">your Library</a>.</span></p></ng-template>
      <button class="rf-text-link order-card__cancel" type="button" (click)="onCancel()">Cancel and choose another model</button>
    </div>
  </aside>
</div>

<!-- The full render after payment (Figma 28574:58489): both frames the user has already seen, still
     the preview, on the same display and phone; "when your videos are ready" to their right; the
     wait under them. -->
<section class="processing-panel" *ngIf="job && isRendering">
  <h1 class="flow-title">Rendering your videos.</h1>
  <p class="flow-subtitle flow-subtitle--centered">Both orientations, {{ seconds }} seconds each, from <span class="rf-name-with"><strong class="rf-name" [title]="job.name">{{ job.name }}</strong>.</span></p>

  <div class="rendering-stage">
    <div class="processing-devices">
      <app-render-still-devices *ngIf="job.preview as preview; else fileCard" [landscape]="preview.landscape" [portrait]="preview.portrait" [name]="job.name" [veiled]="veil"></app-render-still-devices>
      <ng-template #fileCard>
        <div class="rf-stage rf-stage--landscape processing-stage">
          <div class="processing-file">
            <svg class="processing-file__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>
            <span class="rf-name" [title]="sourceLabel">{{ sourceLabel }}</span>
          </div>
        </div>
      </ng-template>
    </div>

    <!-- Her two ticks, 50px apart, right-aligned with the page: the label, a note under it, the ring. -->
    <div class="render-notify" role="group" aria-label="When your videos are ready">
      <label class="render-notify__row" [class.is-off]="!email">
        <span class="render-notify__text">
          Email me when they’re ready
          <small [title]="email || ''">{{ email || 'No email on this account' }}</small>
        </span>
        <input class="flow-check" type="checkbox" [checked]="emailOn" [disabled]="!email" (change)="onEmailChange($event)">
      </label>
      <label class="render-notify__row" [class.is-off]="browserBlocked">
        <span class="render-notify__text">
          Notify me in this browser
          <small *ngIf="browserNote">{{ browserNote }}</small>
        </span>
        <input class="flow-check" type="checkbox" [checked]="browserOn" [disabled]="browserBlocked || askingBrowser" (change)="onBrowserChange($event)">
      </label>
      <p *ngIf="isMock && email" class="render-notify__stand-in">Stand-in: no email is sent until the render backend is connected.</p>
    </div>
  </div>

  <div class="progress-track" role="progressbar" aria-label="Video render" aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="job.progress">
    <div class="progress-fill" [style.width.%]="job.progress"></div>
  </div>
  <p class="process-status" role="status">{{ job.statusText }}</p>
  <p class="rendering-hint">{{ etaLine }} You can leave this page — the videos also appear in <a routerLink="/render/library">your Library</a> when they are done.</p>
</section>
